import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllReviews, deleteReview, getProducts } from '../../firebase/database';
import { formatDate } from '../../utils/helpers';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const Stars = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array(5).fill(0).map((_, i) => (
      <svg key={i} className={`w-3.5 h-3.5 ${i < rating ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </div>
);

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [revs, prods] = await Promise.all([getAllReviews(), getProducts()]);
      setReviews(revs);
      const map = {};
      prods.forEach(p => { map[p.id] = p; });
      setProducts(map);
    } catch (err) {
      console.error('Failed to load reviews:', err);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleDelete = async (review) => {
    if (!window.confirm(`Delete this review by "${review.name || 'Anonymous'}"? This cannot be undone.`)) return;
    setDeletingId(review.id);
    try {
      await deleteReview(review.id, review.productId);
      setReviews(prev => prev.filter(r => r.id !== review.id));
      toast.success('Review deleted — product rating updated');
    } catch (err) {
      console.error('Delete review failed:', err);
      toast.error('Failed to delete: ' + (err.message || 'Check your admin permissions.'));
    } finally {
      setDeletingId(null);
    }
  };

  const searchLower = search.trim().toLowerCase();
  const filtered = reviews.filter(r => {
    const matchRating = ratingFilter === 'all' || r.rating === parseInt(ratingFilter);
    const productTitle = products[r.productId]?.title || '';
    const matchSearch = !searchLower ||
      r.comment?.toLowerCase().includes(searchLower) ||
      r.name?.toLowerCase().includes(searchLower) ||
      productTitle.toLowerCase().includes(searchLower);
    return matchRating && matchSearch;
  });

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '0.0';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-black text-gray-900">Reviews</h1>
          <p className="text-gray-500 text-sm">{reviews.length} total reviews · {avgRating} average rating</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-64" placeholder="Search by product, name, or comment..." />
        </div>
        <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} className="input-field w-40">
          <option value="all">All Ratings</option>
          {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Star{r !== 1 ? 's' : ''}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <svg className="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
            {search || ratingFilter !== 'all' ? 'No reviews match your filters' : 'No reviews yet'}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((review, i) => {
              const product = products[review.productId];
              return (
                <motion.div key={review.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                  className="p-5 hover:bg-gray-50/80 transition-colors flex gap-4">
                  <div className="w-12 h-12 bg-[#1a3a6e] text-white rounded-full flex items-center justify-center font-black flex-shrink-0">
                    {review.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{review.name || 'Anonymous'}</p>
                        <p className="text-xs text-gray-400">{formatDate(review.createdAt)}</p>
                      </div>
                      <Stars rating={review.rating} />
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed mb-2">{review.comment}</p>
                    {product ? (
                      <Link to={`/product/${product.id}`} target="_blank" className="inline-flex items-center gap-2 text-xs font-semibold text-[#1a3a6e] hover:text-[#e63946] transition-colors">
                        <img src={product.images?.[0] || 'https://via.placeholder.com/24'} alt="" className="w-5 h-5 object-cover rounded" onError={e => e.target.src = 'https://via.placeholder.com/24'} />
                        {product.title}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                      </Link>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Product no longer exists</p>
                    )}
                  </div>
                  <button onClick={() => handleDelete(review)} disabled={deletingId === review.id}
                    title="Delete review" className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors flex-shrink-0 disabled:opacity-40 self-start">
                    {deletingId === review.id ? <Spinner size="sm" /> : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
